<template>
  <div class="file-merge-page page-container">
    <section class="page-header">
      <h1>文件合并</h1>
      <p>按指定顺序合并多个文本文件</p>
    </section>
    <el-card shadow="never">
      <div slot="header"><span>文件合并工具</span></div>
      <el-upload
        ref="uploader" drag multiple :auto-upload="false" :show-file-list="false"
        :limit="maxFileCount"
        :on-change="handleFileChange"
        :on-exceed="handleFileExceed"
        :file-list="fileList"
        accept=".txt,.csv,.log,.json,.js,.java,.py,.xml,.html,.css,.sql,.md,.yml,.yaml,.properties,.conf,.ini"
      >
        <i class="el-icon-upload" />
        <div class="el-upload__text">将文件拖到此处，或<em>点击选择</em></div>
        <div slot="tip" class="el-upload__tip">文件将严格按照下方列表顺序读取和写入，最多30个文件</div>
      </el-upload>

      <div v-if="fileList.length" class="ordered-files">
        <div class="list-header"><strong>合并顺序</strong><span>可以使用箭头调整</span></div>
        <div v-for="(file, index) in fileList" :key="file.uid" class="file-row">
          <span class="order-number">{{ index + 1 }}</span>
          <span class="file-name" :title="file.name">{{ file.name }}</span>
          <span class="file-size">{{ formatSize(file.size || 0) }}</span>
          <el-button type="text" icon="el-icon-top" :disabled="index === 0 || merging" title="上移" @click="moveFile(index, -1)" />
          <el-button type="text" icon="el-icon-bottom" :disabled="index === fileList.length - 1 || merging" title="下移" @click="moveFile(index, 1)" />
          <el-button type="text" class="remove-button" icon="el-icon-delete" :disabled="merging" title="移除" @click="removeFile(index)" />
        </div>
      </div>

      <el-form v-if="fileList.length" label-position="top" class="merge-options">
        <el-form-item label="自定义分隔符">
          <el-input v-model="separator" maxlength="200" show-word-limit placeholder="例如：---；留空表示不添加分隔符" />
        </el-form-item>
        <el-form-item>
          <el-checkbox v-model="addFileName">添加文件名作为标题</el-checkbox>
          <div class="option-tip">标题会写在分隔符之后、对应文件内容之前</div>
        </el-form-item>
      </el-form>

      <div v-if="fileList.length || mergedContent" class="action-buttons">
        <el-button type="primary" :loading="merging" :disabled="!fileList.length" @click="handleMerge">合并并预览</el-button>
        <el-button :disabled="!mergedContent || merging" @click="handleDownload">下载合并文件</el-button>
        <el-button type="danger" plain :disabled="merging" @click="clearAll">清空</el-button>
      </div>

      <div v-if="mergedContent" class="preview-section">
        <el-divider content-position="left">预览（前100行）</el-divider>
        <pre class="preview-content">{{ previewContent }}</pre>
        <div v-if="mergeStats" class="merge-stats">
          <el-tag type="info">文件数：{{ mergeStats.fileCount }}</el-tag>
          <el-tag type="info">总行数：{{ mergeStats.totalLines }}</el-tag>
          <el-tag type="info">原文件大小：{{ formatSize(mergeStats.totalSize) }}</el-tag>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { mergeFiles } from '../api/fileMerge'

export default {
  name: 'FileMergePage',
  data() {
    return {
      fileList: [],
      separator: '',
      addFileName: false,
      merging: false,
      mergedContent: '',
      mergeStats: null,
      maxFileCount: 30
    }
  },
  computed: {
    previewContent() { return this.mergedContent ? this.mergedContent.split('\n').slice(0, 100).join('\n') : '' }
  },
  methods: {
    handleFileChange(file, files) {
      if (files.length > this.maxFileCount) {
        this.fileList = files.slice(0, this.maxFileCount)
        if (this.$refs.uploader) {
          this.$refs.uploader.uploadFiles = this.fileList
        }
        this.$message.warning(`单次最多合并${this.maxFileCount}个文件，已自动保留前${this.maxFileCount}个`)
      } else {
        this.fileList = files.slice()
      }
      this.clearResult()
    },
    handleFileExceed() {
      this.$message.warning(`单次最多合并${this.maxFileCount}个文件，请减少文件数量后重试`)
    },
    moveFile(index, offset) {
      const target = index + offset
      if (target < 0 || target >= this.fileList.length) return
      const ordered = this.fileList.slice()
      const item = ordered.splice(index, 1)[0]
      ordered.splice(target, 0, item)
      this.fileList = ordered
      this.clearResult()
    },
    removeFile(index) {
      this.fileList = this.fileList.filter((file, current) => current !== index)
      this.clearResult()
    },
    clearResult() {
      this.mergedContent = ''
      this.mergeStats = null
    },
    clearAll() {
      if (this.$refs.uploader) this.$refs.uploader.clearFiles()
      this.fileList = []
      this.separator = ''
      this.addFileName = false
      this.clearResult()
      this.$message.info('已清空文件和合并结果')
    },
    async handleMerge() {
      if (!this.fileList.length) return
      this.merging = true
      try {
        const orderedFiles = this.fileList.map(file => file.raw)
        const { data } = await mergeFiles(orderedFiles, this.separator, this.addFileName)
        if (data.code !== 200) throw new Error(data.message || '合并失败')
        this.mergedContent = data.data.content
        this.mergeStats = data.data
        this.$message.success('已按列表顺序合并')
      } catch (error) {
        const message = error.response && error.response.data ? error.response.data.message : error.message
        this.$message.error(message || '合并失败')
      } finally {
        this.merging = false
      }
    },
    handleDownload() {
      if (!this.mergedContent) return
      const blob = new Blob([this.mergedContent], { type: 'text/plain;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `merged_${Date.now()}.txt`
      link.click()
      window.URL.revokeObjectURL(url)
    },
    formatSize(bytes) {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }
  }
}
</script>

<style scoped>
/* 内容较窄的工具页：覆盖容器的 1280px 限宽为 900px */
.file-merge-page { max-width: 900px; padding-top: 32px; padding-bottom: 80px; }
.file-merge-page .el-card { margin-top: 32px; }
.ordered-files { margin-top: 20px; border: 1px solid var(--line); border-radius: var(--r-md); overflow: hidden; }
.list-header, .file-row { display: flex; align-items: center; gap: 10px; padding: 10px 14px; }
.list-header { justify-content: space-between; color: var(--ink-2); background: var(--surface-2); }
.list-header span, .option-tip { color: var(--ink-3); font-size: 12px; }
.file-row + .file-row { border-top: 1px solid var(--line-soft); }
.order-number { display: inline-flex; justify-content: center; align-items: center; width: 26px; height: 26px; color: #fff; background: var(--brand); border-radius: 50%; flex: none; }
.file-name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
.file-size { color: var(--ink-3); font-size: 12px; }
.remove-button { color: var(--state-error); }
.merge-options, .action-buttons, .preview-section { margin-top: 20px; }
.action-buttons { display: flex; gap: 12px; flex-wrap: wrap; }
.preview-content { max-height: 400px; padding: 16px; overflow: auto; font-size: 13px; line-height: 1.6; white-space: pre-wrap; word-break: break-all; background: var(--surface-2); border: 1px solid var(--line); border-radius: var(--r-md); }
.merge-stats { display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap; }
</style>
